import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  Upload, 
  FileText, 
  X, 
  Plus, 
  ArrowRight,
  Loader2,
  AlertCircle,
  FileSpreadsheet,
  FileImage,
  File
} from "lucide-react";
import Navbar from "../components/Navbar";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const NewComparison = ({ user }) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [vendors, setVendors] = useState([]);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState({});
  const [currentVendorName, setCurrentVendorName] = useState("");

  const getFileIcon = (type) => {
    if (type?.includes("pdf")) return <FileText className="w-8 h-8 text-red-400" />;
    if (type?.includes("excel") || type?.includes("csv") || type?.includes("spreadsheet")) 
      return <FileSpreadsheet className="w-8 h-8 text-green-400" />;
    if (type?.includes("image")) return <FileImage className="w-8 h-8 text-blue-400" />;
    return <File className="w-8 h-8 text-gray-400" />;
  };

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const vendorId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const vendorName = currentVendorName || file.name.replace(/\.[^/.]+$/, "");
      
      setVendors((prev) => [
        ...prev,
        {
          id: vendorId,
          name: vendorName,
          file: file,
          status: "pending"
        }
      ]);
    });
    setCurrentVendorName("");
  }, [currentVendorName]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'image/*': ['.png', '.jpg', '.jpeg', '.webp']
    },
    multiple: true
  });

  const removeVendor = (vendorId) => {
    setVendors((prev) => prev.filter((v) => v.id !== vendorId));
  };

  const updateVendorName = (vendorId, newName) => {
    setVendors((prev) => 
      prev.map((v) => v.id === vendorId ? { ...v, name: newName } : v)
    );
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Please enter a comparison name");
      return;
    }
    
    if (vendors.length < 2) {
      toast.error("Please add at least 2 vendors to compare");
      return;
    }

    setCreating(true);
    
    try {
      const token = localStorage.getItem("auth_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Create comparison
      const createResponse = await axios.post(
        `${API}/comparisons`,
        { name, description },
        { headers, withCredentials: true }
      );
      
      const comparisonId = createResponse.data.comparison_id;

      // Upload each vendor
      for (const vendor of vendors) {
        setUploading((prev) => ({ ...prev, [vendor.id]: true }));
        
        try {
          const formData = new FormData();
          formData.append("file", vendor.file);

          await axios.post(
            `${API}/comparisons/${comparisonId}/vendors?vendor_name=${encodeURIComponent(vendor.name)}`,
            formData,
            { 
              headers: { ...headers, "Content-Type": "multipart/form-data" },
              withCredentials: true
            }
          );
          
          setVendors((prev) => 
            prev.map((v) => v.id === vendor.id ? { ...v, status: "uploaded" } : v)
          );
        } catch (error) {
          console.error(`Error uploading vendor ${vendor.name}:`, error);
          setVendors((prev) => 
            prev.map((v) => v.id === vendor.id ? { ...v, status: "error" } : v)
          );
        } finally {
          setUploading((prev) => ({ ...prev, [vendor.id]: false }));
        }
      }

      // Run matching
      await axios.post(
        `${API}/comparisons/${comparisonId}/match`,
        {},
        { headers, withCredentials: true }
      );

      toast.success("Comparison created and analyzed!");
      navigate(`/comparison/${comparisonId}`);
    } catch (error) {
      console.error("Error creating comparison:", error);
      toast.error(error.response?.data?.detail || "Failed to create comparison");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020408]">
      <Navbar user={user} />

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-10">
            <h1 className="font-display text-4xl font-bold tracking-tight mb-2">
              New <span className="text-primary-main">Comparison</span>
            </h1>
            <p className="text-gray-400">
              Upload vendor quotes and let AI analyze them for you
            </p>
          </div>

          {/* Form */}
          <div className="space-y-8">
            {/* Comparison Details */}
            <div className="glass rounded-xl p-6 space-y-6">
              <h2 className="font-display text-xl font-semibold">Comparison Details</h2>
              
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-300">Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Office Supplies Q4 2024"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="comparison-name-input"
                  className="bg-background-subtle border-border focus:border-primary-main"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-gray-300">Description (optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of what you're comparing..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  data-testid="comparison-description-input"
                  className="bg-background-subtle border-border focus:border-primary-main resize-none h-24"
                />
              </div>
            </div>

            {/* Vendor Name Input */}
            <div className="glass rounded-xl p-6">
              <h2 className="font-display text-xl font-semibold mb-4">Add Vendors</h2>
              
              <div className="space-y-2 mb-4">
                <Label htmlFor="vendorName" className="text-gray-300">Vendor Name (optional)</Label>
                <Input
                  id="vendorName"
                  placeholder="Enter vendor name before uploading..."
                  value={currentVendorName}
                  onChange={(e) => setCurrentVendorName(e.target.value)}
                  data-testid="vendor-name-input"
                  className="bg-background-subtle border-border focus:border-primary-main"
                />
                <p className="text-xs text-gray-500">
                  Enter the vendor name, then drop or select files. If empty, file name will be used.
                </p>
              </div>

              {/* Dropzone */}
              <div
                {...getRootProps()}
                data-testid="file-dropzone"
                className={`drop-zone rounded-xl p-12 text-center cursor-pointer transition-all ${
                  isDragActive ? "drag-over border-primary-main bg-primary-main/5" : ""
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="w-12 h-12 text-primary-main/50 mx-auto mb-4" />
                <p className="text-lg text-gray-300 mb-2">
                  {isDragActive 
                    ? "Drop vendor files here..." 
                    : "Drag & drop vendor files here"
                  }
                </p>
                <p className="text-sm text-gray-500">
                  or click to select files (PDF, Excel, CSV, Word, Images)
                </p>
              </div>
            </div>

            {/* Uploaded Vendors */}
            {vendors.length > 0 && (
              <div className="glass rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-xl font-semibold">
                    Vendors ({vendors.length})
                  </h2>
                  {vendors.length < 2 && (
                    <span className="flex items-center gap-2 text-sm text-amber-400">
                      <AlertCircle className="w-4 h-4" />
                      Add at least 2 vendors
                    </span>
                  )}
                </div>
                
                <div className="space-y-3">
                  {vendors.map((vendor) => (
                    <div
                      key={vendor.id}
                      data-testid={`vendor-card-${vendor.id}`}
                      className="flex items-center gap-4 p-4 bg-background-subtle rounded-lg border border-border"
                    >
                      <div className="flex-shrink-0">
                        {getFileIcon(vendor.file?.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <Input
                          value={vendor.name}
                          onChange={(e) => updateVendorName(vendor.id, e.target.value)}
                          className="bg-transparent border-transparent hover:border-border focus:border-primary-main font-semibold text-lg h-auto p-0"
                          placeholder="Vendor name"
                        />
                        <p className="text-sm text-gray-500 truncate">
                          {vendor.file?.name} • {(vendor.file?.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {uploading[vendor.id] && (
                          <Loader2 className="w-5 h-5 animate-spin text-primary-main" />
                        )}
                        {vendor.status === "uploaded" && (
                          <span className="text-accent-success text-sm">Analyzed</span>
                        )}
                        {vendor.status === "error" && (
                          <span className="text-red-400 text-sm">Error</span>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeVendor(vendor.id)}
                          className="text-gray-500 hover:text-red-400 hover:bg-red-500/10"
                          disabled={creating}
                        >
                          <X className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="border-border hover:bg-background-subtle"
                disabled={creating}
              >
                Cancel
              </Button>
              <Button
                data-testid="create-comparison-btn"
                onClick={handleCreate}
                disabled={creating || vendors.length < 2 || !name.trim()}
                className="btn-skew bg-primary-main text-black font-bold uppercase tracking-wider px-8 py-5 hover:bg-primary-light hover:shadow-glow-cyan transition-all disabled:opacity-50"
              >
                <span className="flex items-center gap-2">
                  {creating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      Create & Analyze
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NewComparison;
